import { useEffect, useRef } from "react";

// WORK IN PROGRESS SURVIVES BACKING OUT.
//
// HIS PHONE, 2026-10-15: *"I scanned a part tag and started creating children — several of them.
// When I backed out to check, NOTHING had been saved. All that work was lost."*
//
// Backing out of a page unmounts it, and React state dies with the component — there is no villain
// in the code, which is exactly why it has to be designed against. Anything a crew member TYPES is
// kept in `sessionStorage` as they go and handed back when they return, until it is either
// submitted or deliberately discarded.
//
// WHY sessionStorage AND NOT THE SERVER: a half-written child part is not a part. Writing it to the
// database would put unfinished records in front of the rest of the crew and in the counts. This
// keeps it on the one device, for the one person, exactly as long as the tab lives.
//
//   const draft = useDraft({
//     key: `split:${partId}`,
//     enabled: splitOpen,
//     snapshot: () => ({ children: children.map(serialise), note }),   // JSON-safe
//     restore: (d) => { setChildren(d.children.map(rehydrate)); setNote(d.note || ""); },
//     isWorth: (d) => d.children.some((c) => c.tag_code || c.name),
//   });
//   ... draft.clear() after a successful save.
//
// `snapshot` must return something JSON can carry — Sets and Maps are the caller's job to flatten,
// because only the caller knows what is worth keeping.
export function useDraft({ key, enabled = true, snapshot, restore, isWorth, debounceMs = 400 }) {
  const store = typeof window === "undefined" ? null : window.sessionStorage;
  const full = `fas.draft.${key}`;
  const restored = useRef(false);
  const timer = useRef(null);
  const latest = useRef({ snapshot, restore, isWorth });
  latest.current = { snapshot, restore, isWorth };

  // HAND IT BACK, ONCE, when the form opens.
  useEffect(() => {
    if (!enabled || restored.current || !store) return;
    restored.current = true;
    try {
      const raw = store.getItem(full);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (latest.current.isWorth?.(parsed) === false) { store.removeItem(full); return; }
      latest.current.restore?.(parsed);
    } catch { store.removeItem(full); }
  }, [enabled, full, store]);

  useEffect(() => { if (!enabled) restored.current = false; }, [enabled]);

  // KEEP IT AS THEY TYPE — debounced, so a fast scanner does not write on every character.
  useEffect(() => {
    if (!enabled || !store) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try {
        const snap = latest.current.snapshot?.();
        if (!snap || latest.current.isWorth?.(snap) === false) { store.removeItem(full); return; }
        store.setItem(full, JSON.stringify({ ...snap, _at: new Date().toISOString() }));
      } catch { /* a full or blocked store must never break the form */ }
    }, debounceMs);
    return () => clearTimeout(timer.current);
  });

  const clear = () => { try { store?.removeItem(full); } catch { /* ignore */ } };
  const peek = () => {
    try { return JSON.parse(store?.getItem(full) || "null"); } catch { return null; }
  };
  return { clear, peek, has: () => !!peek() };
}

// WARN BEFORE THE TAB GOES. Covers refresh and closing the tab, which no in-app guard can catch.
export function useLeaveWarning(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const onLeave = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [enabled]);
}
